import { ToolFunction } from "@/types/app/tool";
import { useEffect, useState } from "react";
import Tag, { TagSize } from "@components/library/v2/Tag";
import { ClickToCopyWrapper } from "@components/library/IdDisplay";
import { HoverPopover } from "@components/library/Entities/shared/HoverPopover";

export const ToolFunctionWithPopover = ({
  toolFunction,
  size = 20,
}: {
  toolFunction: ToolFunction;
  size?: TagSize;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [hideOverflowParams, setHideOverflowParams] = useState(true);

  // reset hideOverflowParams when the popover is closed
  useEffect(() => {
    if (isPopoverOpen === false) {
      setHideOverflowParams(true);
    }
  }, [isPopoverOpen]);

  const toolName = toolFunction?.name;
  const toolDescription = toolFunction?.description || "No description";
  const toolParameters = toolFunction?.parameters?.properties
    ? Object.keys(toolFunction?.parameters?.properties)
    : null;

  const numParams = toolParameters?.length ?? 0;
  const MIN_PARAMS_TO_HIDE = 3;
  const paramsToShow = hideOverflowParams ? toolParameters?.slice(0, MIN_PARAMS_TO_HIDE) : toolParameters;

  const tag = (
    <Tag shade="violet" className="font-mono font-medium" size={size}>
      {toolName}
    </Tag>
  );

  const popoverContent = (
    <div className="flex w-320 max-w-sm flex-col gap-16 rounded-lg bg-background-base-1 p-12 text-text-base-1 shadow-card-sm">
      <div className="text-12-16 font-medium">
        <ClickToCopyWrapper name="Function Name" textToCopy={toolName}>
          {toolName}
        </ClickToCopyWrapper>
        <div className="mt-6 whitespace-pre-wrap text-pretty text-12-16 font-normal text-text-base-2">
          {toolDescription}
        </div>
      </div>

      <div className="flex flex-wrap gap-6 text-12-12 font-medium">
        {paramsToShow?.map((parameter) => (
          <Tag key={parameter} shade="gray" size={18}>
            {parameter}
          </Tag>
        ))}
        {hideOverflowParams && numParams > MIN_PARAMS_TO_HIDE && (
          <Tag size={18} onClick={() => setHideOverflowParams(false)} shade="blank" interactive>
            +{numParams - MIN_PARAMS_TO_HIDE}
          </Tag>
        )}
      </div>
    </div>
  );

  return (
    <HoverPopover
      trigger={tag}
      content={popoverContent}
      side="bottom"
      align="start"
      openDelay={100}
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
    />
  );
};
